import { Drawer, SxProps, Theme, useTheme } from "@mui/material";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useResizeObserver } from "../../../utils/useResizeObserver";
import { sxClsx } from "utils/styleClsx";

function isVerticalDrawer(props: Props) {
    return props.anchor === "left" || props.anchor === "right";
}

export interface DrawerStore {
    setOpen(isOpen: boolean);
    reset();
}

interface OwnProps {
    anchor: "left" | "top" | "right" | "bottom";
    offsetPosition?: "Left" | "Top" | "Right" | "Bottom";
    offsetPx?: number;
    miniSizePx?: number;
    maxSizePx?: number;
    open: boolean;
    content?: React.ReactNode;
    alternateWidth?: number;
    alternateLeftPosition?: number;
    variant?: "permanent" | "persistent" | "temporary";
    onDrawerClose?: () => void;
    onDrawerOpen?: () => void;
    collapsible: "mini" | "full";
    hideBackdrop?: boolean;
    disableBackdropClick?: boolean;
    disableBackdrop?: boolean;
    transitionDuration?: React.ComponentProps<
        typeof Drawer
    >["transitionDuration"];

    // drawer stays closed while drawer-content is loading
    loading?: boolean;
    drawerStore?: DrawerStore;
    customRootStyles?: React.CSSProperties;
    customPaperClasses?: string;
    customPaperSx?: SxProps<Theme>;
    onDrawerResize?: (width: number, height: number) => void;
    disableEnforceFocus?: boolean;
}

type Props = OwnProps;

const AcxDrawer: React.FC<Props> = (props) => {
    const location = useLocation();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const resizeObj = useResizeObserver<HTMLDivElement>({
        name: "AcxDrawer",
        pixelDelta: 1,
        onResize: props.onDrawerResize,
    });

    useEffect(() => {
        props.drawerStore?.reset();
    }, [location.pathname, props.drawerStore]);

    const drawerOpen = props.open;
    const theme = useTheme();
    return (
        <Drawer
            ref={resizeObj.theRef}
            style={{
                ...props.customRootStyles,
                ...{ visibility: props.loading ? "hidden" : "visible" },
            }}
            variant={props.variant}
            elevation={3}
            sx={sxClsx(
                {
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    position: "absolute",
                    overflowY: isVerticalDrawer(props) ? "auto" : "auto",
                    overflowX: isVerticalDrawer(props) ? "hidden" : "auto",
                    top: `calc(100% - ${
                        (props.open ? props.maxSizePx : props.miniSizePx) ?? 0
                    }px)`,
                    left:
                        props.alternateWidth !== undefined
                            ? `${props.alternateLeftPosition}px`
                            : undefined,
                },

                drawerOpen && {
                    width: isVerticalDrawer(props)
                        ? "auto"
                        : props.alternateWidth !== undefined
                        ? `${props.alternateWidth}px`
                        : "100%",
                    height: isVerticalDrawer(props)
                        ? "100%"
                        : props.maxSizePx !== undefined
                        ? `${props.maxSizePx}px`
                        : `auto`,

                    overflowY: isVerticalDrawer(props) ? "auto" : "auto",
                    overflowX: isVerticalDrawer(props) ? "hidden" : "auto",
                },

                !drawerOpen &&
                    props.collapsible === "mini" && {
                        width: isVerticalDrawer(props)
                            ? `${props.miniSizePx ?? 0}px`
                            : props.alternateWidth !== undefined
                            ? `${props.alternateWidth}px`
                            : "100%",
                        top:
                            props.anchor === "bottom"
                                ? `calc(100% - ${props.miniSizePx ?? 0}px)`
                                : "auto",
                        height: isVerticalDrawer(props)
                            ? "100%"
                            : `${props.miniSizePx ?? 0}px`,

                        overflowY: isVerticalDrawer(props) ? "auto" : "auto",
                        overflowX: isVerticalDrawer(props) ? "hidden" : "auto",
                    },

                !drawerOpen &&
                    props.collapsible === "full" && {
                        width: isVerticalDrawer(props) ? 0 : "100%",
                        height: isVerticalDrawer(props) ? "100%" : 0,
                    },
            )}
            PaperProps={{
                sx: sxClsx(
                    {
                        left:
                            props.alternateWidth !== undefined
                                ? `${props.alternateLeftPosition}px`
                                : undefined,

                        boxShadow: theme.shadows[5],
                        [`padding${props.offsetPosition ?? ""}`]: `${
                            props.offsetPx ?? 0
                        }px`,
                    },
                    drawerOpen && {
                        width: isVerticalDrawer(props)
                            ? "auto"
                            : props.alternateWidth !== undefined
                            ? `${props.alternateWidth}px`
                            : "100%",
                        height: isVerticalDrawer(props)
                            ? "100%"
                            : props.maxSizePx !== undefined
                            ? `${props.maxSizePx}px`
                            : `auto`,

                        overflowY: isVerticalDrawer(props) ? "auto" : "auto",
                        overflowX: isVerticalDrawer(props) ? "hidden" : "auto",
                    },
                    !drawerOpen &&
                        props.collapsible === "mini" && {
                            width: isVerticalDrawer(props)
                                ? `${props.miniSizePx ?? 0}px`
                                : props.alternateWidth !== undefined
                                ? `${props.alternateWidth}px`
                                : "100%",
                            top:
                                props.anchor === "bottom"
                                    ? `calc(100% - ${props.miniSizePx ?? 0}px)`
                                    : "auto",
                            height: isVerticalDrawer(props)
                                ? "100%"
                                : `${props.miniSizePx ?? 0}px`,

                            overflowY: isVerticalDrawer(props)
                                ? "auto"
                                : "auto",
                            overflowX: isVerticalDrawer(props)
                                ? "hidden"
                                : "auto",
                        },
                    !drawerOpen &&
                        props.collapsible === "full" && {
                            width: isVerticalDrawer(props) ? 0 : "100%",
                            height: isVerticalDrawer(props) ? "100%" : 0,
                        },
                    props.customPaperSx,
                ),
            }}
            ModalProps={{
                keepMounted: true,
                hideBackdrop: props.disableBackdrop ?? true,
                onClose: (event, reason) => {
                    if (
                        reason === "backdropClick" &&
                        props.disableBackdropClick
                    )
                        return;
                    props.onDrawerClose?.();
                },
                disableEnforceFocus: props.disableEnforceFocus,
            }}
            BackdropProps={{ invisible: props.disableBackdrop ?? true }}
            SlideProps={{ mountOnEnter: true }}
            anchor={props.anchor}
            open={drawerOpen}
            onClose={props.onDrawerClose}
            transitionDuration={props.transitionDuration}
            // transitionDuration={{ exit: 500, appear: 500, enter: 500 }}
        >
            {props.content}
        </Drawer>
    );
};

export default AcxDrawer;
